<template>
  <v-col class="pa-0">
    <v-row class="ma-0 pa-0">
      <v-col align="center" cols="8" offset="2">
        <h2>{{ $translate('RESET_PASS_TITLE') }}</h2>
      </v-col>
      <v-col class="text-right">
        <v-icon size="20" @click="onCloseBtnClick"> ti-close </v-icon>
      </v-col>
    </v-row>

    <!-- Text to display after password is changed -->
    <v-row v-if="passwordChanged" class="mx-3 my-0">
      <v-col align="center">
        <v-icon size="140" color="#a7dc86"> done_outline </v-icon>
        <p class="mt-2">{{ $translate('PASS_CHANGED_DESC') }}</p>
      </v-col>
    </v-row>

    <v-form v-else v-model="valid" ref="form" lazy-validation>
      <v-row class="mx-3 my-0">
        <!-- Old password -->
        <v-col v-if="userLoggedin" cols="12" class="pt-2 pb-0">
          <v-text-field
            v-model="oldPassword"
            :rules="passwordRules"
            :label="$translate('OLD_PASS')"
            outlined
            required
            hide-details="auto"
            :append-icon="hideOldPassword ? 'visibility' : 'visibility_off'"
            @click:append="() => (hideOldPassword = !hideOldPassword)"
            :type="hideOldPassword ? 'password' : 'text'"
          />
        </v-col>
        <!-- New password -->
        <v-col cols="12" class="pt-2 pb-0">
          <v-text-field
            v-model="newPassword"
            :rules="newPasswordRules"
            :label="$translate('NEW_PASS')"
            outlined
            required
            hide-details="auto"
            :append-icon="hideNewPassword ? 'visibility' : 'visibility_off'"
            @click:append="() => (hideNewPassword = !hideNewPassword)"
            :type="hideNewPassword ? 'password' : 'text'"
          />
        </v-col>
        <!-- Confirm new password -->
        <v-col cols="12" class="pt-2 pb-0">
          <v-text-field
            v-model="newPasswordConfirm"
            :rules="passwordConfirmRules"
            :label="$translate('CONFIRM_NEW_PASS')"
            outlined
            required
            hide-details="auto"
            :append-icon="
              hideNewPasswordConfirm ? 'visibility' : 'visibility_off'
            "
            @click:append="
              () => (hideNewPasswordConfirm = !hideNewPasswordConfirm)
            "
            :type="hideNewPasswordConfirm ? 'password' : 'text'"
          />
        </v-col>
        <v-col cols="12">
          <v-alert
            v-if="errorMessage !== ''"
            color="red"
            dense
            text
            type="error"
            >{{ errorMessage }}</v-alert
          >
          <v-btn
            color="primary"
            depressed
            block
            large
            class="pt-1 primary-font-color"
            @click="changePassword"
            :loading="loading"
          >
            {{ saveBtnText }}
          </v-btn>
        </v-col>
        <v-divider />
      </v-row>
    </v-form>

    <v-card v-if="!userLoggedin" flat color="#d9d9d9" class="mt-3">
      <v-row class="mx-3 pa-4 my-0" align="center" justify="center">
        <v-col align="center" justify="center" class="pa-0">
          <v-btn
            color="primary"
            class="primary-font-color"
            depressed
            large
            @click="onLoginBtnClick"
          >
            {{ $translate('LOGIN') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-col>
</template>

<script>
import doRequest from '@/api/dssRequest';

export default {
  name: 'AccountChangePassword',
  computed: {
    userLoggedin() {
      const isAuthenticated = this.$store.getters['auth/isValid'];
      if (isAuthenticated) {
        return true;
      }
      return false;
    },
    saveBtnText() {
      if (this.userLoggedin) {
        return this.$translate('SAVE');
      }
      return this.$translate('SAVE_AND_LOGIN');
    },
  },
  data() {
    return {
      oldPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
      passwordRules: [(v) => !!v || this.$translate('PASSWORD_REQUIRED')],
      newPasswordRules: [
        (v) => !!v || this.$translate('PASSWORD_REQUIRED'),
        (v) =>
          (v.length >= 8 && v.length <= 48) ||
          this.$translate('PASS_LENGTH_LIMIT'),
      ],
      passwordConfirmRules: [
        (v) =>
          (!!v && v.trim() === this.newPassword.trim()) ||
          this.$translate('PASS_NOT_MATCH'),
      ],
      hideOldPassword: true,
      hideNewPassword: true,
      hideNewPasswordConfirm: true,
      loading: false,
      valid: null,
      errorMessage: '',
      userIdNoLogin: '',
      passwordChanged: false,
    };
  },
  methods: {
    changePassword() {
      // Save the new password and login upon success
      if (this.$refs.form.validate()) {
        this.loading = true;

        const payload = {
          NewPassword: this.newPassword,
        };

        let request = 'changePasswordNoLogin';
        let requestObj = this.$store.getters['auth/userName'];

        if (this.userLoggedin) {
          payload.OldPassword = this.oldPassword;
          request = 'changePassword';
        } else {
          // Token to change password for non-loggedin user
          const { token } = this.$route.query;
          payload.PasswordResetToken = token;
          this.userIdNoLogin = this.$route.query.UserID;
          requestObj = this.userIdNoLogin;
        }

        doRequest(
          this.onChangePasswordSucceed,
          this.onChangePasswordFail,
          request,
          requestObj,
          payload
        );
      } else {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
      }
    },
    onLoginBtnClick() {
      this.$emit('triggerPopup', {
        toggle: true,
        component: 'Login',
      });
    },
    onChangePasswordSucceed(response) {
      this.loading = false;
      this.passwordChanged = true;

      if (!this.userLoggedin) {
        // Auto-login for non-loggedin user
        this.$store.commit('auth/setUserName', this.userIdNoLogin);
        this.$store.commit('auth/setToken', response.data.AccessToken);
        this.$store.commit('auth/setRefreshToken', response.data.RefreshToken);
      }

      this.onCloseBtnClick();
    },
    onChangePasswordFail(error) {
      this.loading = false;
      this.errorMessage = this.$translateError(error.data.ErrorCode);
    },
    onCloseBtnClick() {
      this.$emit('triggerPopup', { toggle: false, component: 'Login' });
      this.$emit('close');
    },
  },
};
</script>
